import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { selectedHitContext } from './SelectedHitProvider';
import { useAuth } from '../AuthContext';
import useClientList from '../utilities/useClientList';
import styles from './style.module.css';
import DisplayedGrant from './DisplayedGrant';
import NoGrantDisplay from './NoGrantDisplayed';

// Step 30: column 3 drawn to the owner's picture. The rows line up with
// column 2: the top slot (the count-line row) and the first row are left
// blank, the ADD MORE CLIENTS box shares the buttons row, and the panel's
// top is level with the first card. Every control keeps its handler.
//
// ADD MORE CLIENTS (owner, 2026-09-04): clicking the box opens a panel
// listing ALL the user's clients — the same list column 1 reads — each with
// a checkbox; several can be ticked, and the ticked names show in the box
// after the label. The choice is kept in the browser (localStorage, under a
// key that carries the signed-in user's id); there is no server route for
// it, so nothing is sent anywhere.
const STORAGE_PREFIX = 'AddMoreClients:';

function readStoredIds(key) {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (e) {
    return [];
  }
}

function writeStoredIds(key, ids) {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch (e) {
    // storage may be unavailable (private window); the choice then lives
    // for this page view only
  }
}

export default function GrantView({ currentClient }) {
  const navigate = useNavigate();
  const context = useContext(selectedHitContext);
  if (!context) {
    throw new Error(
      'selectedHitContext.Provider is missing. Wrap your component tree with <SelectedHitProvider>'
    );
  }
  const { selectedHit } = context;

  const { user } = useAuth();
  const clients = useClientList();
  const userKey =
    (user && user.uid) ||
    (typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('user_id')
      : null) ||
    'anonymous';
  const storageKey = `${STORAGE_PREFIX}${userKey}`;

  const [clientsOpen, setClientsOpen] = useState(false);
  const [chosenIds, setChosenIds] = useState(() => readStoredIds(storageKey));
  const boxRef = useRef(null);

  // A different signed-in user has a different remembered choice.
  useEffect(() => {
    setChosenIds(readStoredIds(storageKey));
  }, [storageKey]);

  // Clicking outside the box and its panel closes the panel.
  useEffect(() => {
    if (!clientsOpen) return undefined;
    const onDocumentMouseDown = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setClientsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [clientsOpen]);

  const toggleClient = (clientId) => {
    const id = String(clientId);
    setChosenIds((previous) => {
      const next = previous.includes(id)
        ? previous.filter((x) => x !== id)
        : [...previous, id];
      writeStoredIds(storageKey, next);
      return next;
    });
  };

  const chosenNames = clients
    .filter((client) => chosenIds.includes(String(client.client_id)))
    .map((client) => client.organization_name)
    .filter(Boolean);

  const handleCheckEligibility = () => {
    if (!selectedHit || !currentClient) return;

    navigate('/form', {
      state: {
        grantDbGrant: selectedHit,
        currentClient,
      },
    });
  };

  return (
    <div className={styles.rightRoot}>
      {/* Row: ADD MORE CLIENTS — the box and, when open, the panel under it */}
      <div className={styles.rowBoxWrap} ref={boxRef}>
        <button
          type="button"
          className={`${styles.rowBox} ${styles.assignedRow}`}
          onClick={() => setClientsOpen((open) => !open)}
          aria-haspopup="listbox"
          aria-expanded={clientsOpen}
        >
          <span className={styles.assignedLabel}>Add more clients</span>
          <span className={styles.assignedNames} title={chosenNames.join(', ')}>
            {chosenNames.map((name, i) => (
              <span key={`${name}-${i}`} className={styles.assignedName}>{name}</span>
            ))}
          </span>
        </button>

        {clientsOpen && (
          <ul className={styles.clientPanel} role="listbox" aria-multiselectable="true">
            {clients.length === 0 && (
              <li className={`${styles.clientRow} ${styles.clientRowEmpty}`}>No clients yet</li>
            )}
            {clients.map((client) => {
              const id = String(client.client_id);
              const checked = chosenIds.includes(id);
              return (
                <li key={id} className={styles.clientRow} role="option" aria-selected={checked}>
                  <label className={styles.clientOption}>
                    <input
                      type="checkbox"
                      className={styles.clientCheckbox}
                      checked={checked}
                      onChange={() => toggleClient(id)}
                    />
                    <span className={styles.clientCheckmark} aria-hidden="true" />
                    <span className={styles.clientOptionName}>{client.organization_name}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* The panel: header strip with the green button + the scrolling details */}
      <div className={styles.detailPanel}>
        <div className={styles.panelHeader}>
          {/* <button className={styles.proposalBtn}>Create Proposal</button> */}
          <button
            type="button"
            className={styles.proposalBtn}
            onClick={handleCheckEligibility}
            disabled={!selectedHit || !currentClient}
          >
            Check Eligibility
          </button>
        </div>

        <div className={styles.detailScroller}>
          {selectedHit ? (
            <DisplayedGrant selectedHit={selectedHit} />
          ) : (
            <NoGrantDisplay />
          )}
        </div>
      </div>
    </div>
  );
}
